import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SurveyTemplate from "./SurveyTemplate";
import { submitSurveyData, validateSurveyData } from "../services/surveyApi";
import { saveEmail } from "../utils/emailCollection";
import "../styles/components/MultiPageSurvey.css";

const SurveyPage5 = ({
  surveyData,
  updateSurveyData,
  onComplete,
  onBack,
  onNavigationBack,
  currentSection,
  initialQuestionIndex = 0,
  updatePageState,
  pageNumber,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(initialQuestionIndex);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [localData, setLocalData] = useState({
    alcoholConsumption: surveyData.alcoholConsumption || "",
    dietHabits: surveyData.dietHabits || "",
    email: surveyData.email || "",
  });

  const updatePageStateRef = useRef(updatePageState);
  updatePageStateRef.current = updatePageState;
  const lastSyncedQuestion = useRef(null);
  useEffect(() => {
    if (lastSyncedQuestion.current === currentQuestion || !updatePageStateRef.current || !pageNumber) return;
    lastSyncedQuestion.current = currentQuestion;
    updatePageStateRef.current(pageNumber, currentQuestion);
  }, [currentQuestion, pageNumber]);

  const questions = [
    {
      id: "alcoholConsumption",
      question: t("survey.page5.alcoholConsumption.question"),
      helperText: t("survey.page5.alcoholConsumption.helperText"),
      type: "select",
      options: [
        {
          value: "under_7",
          label: t("survey.page5.alcoholConsumption.options.under_7"),
        },
        {
          value: "7_to_14",
          label: t("survey.page5.alcoholConsumption.options.7_to_14"),
        },
        {
          value: "14_to_25",
          label: t("survey.page5.alcoholConsumption.options.14_to_25"),
        },
        {
          value: "25_to_50",
          label: t("survey.page5.alcoholConsumption.options.25_to_50"),
        },
        {
          value: "over_50",
          label: t("survey.page5.alcoholConsumption.options.over_50"),
        },
        {
          value: "none",
          label: t("survey.page5.alcoholConsumption.options.none"),
        },
      ],
      info: t("survey.page5.alcoholConsumption.info"),
    },
    {
      id: "dietHabits",
      question: t("survey.page5.dietHabits.question"),
      type: "select",
      options: [
        {
          value: "yes_high_sodium",
          label: t("survey.page5.dietHabits.options.yes_high_sodium"),
        },
        {
          value: "yes_broiling_salting",
          label: t("survey.page5.dietHabits.options.yes_broiling_salting"),
        },
        {
          value: "yes_saturated_fat",
          label: t("survey.page5.dietHabits.options.yes_saturated_fat"),
        },
        {
          value: "multiple",
          label: t("survey.page5.dietHabits.options.multiple"),
        },
        { value: "no", label: t("survey.page5.dietHabits.options.no") },
        { value: "unsure", label: t("survey.page5.dietHabits.options.unsure") },
      ],
      info: t("survey.page5.dietHabits.info"),
    },
    {
      id: "email",
      question: t("survey.page5.email.question"),
      type: "email",
      placeholder: t("survey.page5.email.placeholder"),
      info: t("survey.page5.email.info"),
    },
  ];

  const handleOptionSelect = (value) => {
    const currentQ = questions[currentQuestion];
    setLocalData((prev) => ({
      ...prev,
      [currentQ.id]: value,
    }));

    // Auto-advance for single-select questions after a brief delay
    if (currentQ.type === "select") {
      setTimeout(() => {
        handleNext();
      }, 300);
    }
  };

  const handleEmailChange = (value) => {
    setLocalData((prev) => ({
      ...prev,
      email: value,
    }));
  };

  const validateEmail = (email) => {
    if (!email.trim()) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNext = async () => {
    const currentQ = questions[currentQuestion];

    // Validate email if it's the email question
    if (
      currentQ.id === "email" &&
      localData.email.trim() &&
      !validateEmail(localData.email)
    ) {
      return; // Don't proceed if email is invalid
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      console.log("🏁 Survey completion initiated");
      console.log("📝 Current local data:", localData);
      console.log("📋 Existing survey data:", surveyData);

      // Save all data first
      Object.keys(localData).forEach((key) => {
        console.log(`💾 Updating survey data: ${key} = ${localData[key]}`);
        updateSurveyData(key, localData[key]);
      });

      // Prepare complete survey data for API submission
      const completeSurveyData = {
        ...surveyData,
        ...localData,
      };
      console.log("🔗 Complete survey data prepared:", completeSurveyData);

      // Validate survey data
      console.log("🔍 Validating survey data...");
      const validation = validateSurveyData(completeSurveyData);
      console.log("✅ Validation result:", validation);

      if (!validation.isValid) {
        console.error("❌ Validation failed:", validation.message);
        setSubmitError(validation.message);
        return;
      }

      // Submit to API
      console.log("📤 Starting API submission...");
      setIsSubmitting(true);
      setSubmitError("");

      try {
        const result = await submitSurveyData(completeSurveyData);
        console.log("📨 API submission result:", result);

        if (result.success) {
          console.log("🎉 Survey data submitted successfully!");
          console.log("📊 API Response data:", result.data);

          // Save email to Firebase if provided
          if (completeSurveyData.email && completeSurveyData.email.trim()) {
            const age = parseInt(completeSurveyData.age, 10);
            const ageRange = age < 30 ? "18-29" : age < 40 ? "30-39" : age < 50 ? "40-49" : age < 60 ? "50-59" : "60+";
            saveEmail(completeSurveyData.email, {
              locale: completeSurveyData.language || "en",
              ageRange,
              sex: completeSurveyData.biologicalSex || null,
              location: completeSurveyData.location || null,
              screeningsCount: result.data?.recommendations?.stronglyRecommend?.length || 0,
            }).catch(() => {}); // fire-and-forget
          }

          // Small delay to show completion message before navigating
          setTimeout(() => {
            console.log("➡️ Navigating to results page...");
            onComplete();
          }, 1000);
        } else {
          console.error("❌ API submission failed:", result.message);
          setSubmitError(result.message);
        }
      } catch (error) {
        console.error("💥 Unexpected error during submission:", error);
        setSubmitError("An unexpected error occurred. Please try again.");
      } finally {
        console.log("🏁 Submission process completed");
        setIsSubmitting(false);
      }
    }
  };

  const handleHeaderBack = () => {
    // Header back button goes to SurveyPage4
    navigate("/survey/4");
  };

  const handleNavigationBack = () => {
    // Navigation back button goes to previous question within the page
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      // If on first question, go to SurveyPage4
      navigate("/survey/4");
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered =
    currentQ.id === "email"
      ? true // Email is optional, so always allow proceeding
      : localData[currentQ.id] &&
        localData[currentQ.id].toString().trim() !== "";
  const isLastQuestion = currentQuestion === questions.length - 1;
  const isEmailQuestion = currentQ.id === "email";
  const emailError =
    isEmailQuestion &&
    localData.email.trim() &&
    !validateEmail(localData.email);

  const surveyConfig = {
    introText: isEmailQuestion
      ? t("survey.page5.congratulations")
      : t("survey.page5.intro"),
    questionText: currentQ.question,
    options: currentQ.options || [],
    infoSections: [
      {
        title: isEmailQuestion
          ? t("survey.page5.whatYoullReceive")
          : t("survey.page5.whyThisMatters"),
        content: currentQ.info,
      },
      {
        title: isEmailQuestion
          ? t("survey.page5.privacy")
          : t("survey.page5.beHonest"),
        content: isEmailQuestion
          ? t("survey.page5.privacyContent")
          : t("survey.page5.honestAnswers"),
      },
    ],
    customInput: isEmailQuestion ? (
      <div className="email-input-container">
        <input
          type="email"
          value={localData.email}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder={currentQ.placeholder}
          className={`email-input ${emailError ? "error" : ""}`}
        />
        {emailError && (
          <span className="email-error-message">
            {t("survey.page5.emailError")}
          </span>
        )}
      </div>
    ) : null,
  };

  return (
    <div className="survey-page-5">
      <SurveyTemplate
        introText={surveyConfig.introText}
        questionText={surveyConfig.questionText}
        helperText={currentQ.helperText || null}
        options={surveyConfig.options}
        infoSections={surveyConfig.infoSections}
        onOptionSelect={handleOptionSelect}
        selectedOption={localData[currentQ.id]}
        onBack={handleHeaderBack}
        infoPanelLayout="collapsible"
        customInput={surveyConfig.customInput}
        progressContainer={
          <div className="progress-container">
            {/* Section Progress */}
            <div className="section-progress">
              <div className="section-indicators">
                <div
                  className={`section-indicator ${
                    currentSection >= 1 ? "active" : ""
                  }`}
                >
                  <span className="section-number">1</span>
                  <span className="section-label">
                    {t("survey.sections.demographics")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 2 ? "active" : ""
                  }`}
                >
                  <span className="section-number">2</span>
                  <span className="section-label">
                    {t("survey.sections.physical")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 3 ? "active" : ""
                  }`}
                >
                  <span className="section-number">3</span>
                  <span className="section-label">
                    {t("survey.sections.family")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 4 ? "active" : ""
                  }`}
                >
                  <span className="section-number">4</span>
                  <span className="section-label">
                    {t("survey.sections.symptoms")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 5 ? "active" : ""
                  }`}
                >
                  <span className="section-number">5</span>
                  <span className="section-label">
                    {t("survey.sections.lifestyle")}
                  </span>
                </div>
              </div>
            </div>

            {/* Question Progress */}
            <div className="question-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                    height: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <span className="progress-text">
                {t("survey.progress", {
                  current: currentQuestion + 1,
                  total: questions.length,
                })}
              </span>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button
              className="nav-button back-button"
              onClick={handleNavigationBack}
            >
              {t("common.back")}
            </button>
            <button
              className="nav-button next-button complete-button"
              onClick={handleNext}
              disabled={!isAnswered || isSubmitting}
            >
              {isSubmitting
                ? t('survey.page5.submittingMessage')
                : isLastQuestion
                ? t('survey.page5.completeSurvey')
                : t('common.next')}
            </button>
          </div>
        }
      />

      {/* Error message */}
      {submitError && (
        <div className="error-message-container">
          <p className="error-text">❌ {submitError}</p>
        </div>
      )}

      {/* Completion message for last question */}
      {isLastQuestion && isAnswered && !isSubmitting && (
        <div className="completion-message">
          <p>{t("survey.page5.completionMessage")}</p>
        </div>
      )}

      {/* Submitting message */}
      {isSubmitting && (
        <div className="submitting-message">
          <p>{t("survey.page5.submittingMessage")}</p>
        </div>
      )}
    </div>
  );
};

export default SurveyPage5;
